import { useEffect } from "react";

// Cookie Consent
import { useCookieConsent } from "components/CookieConsent/useCookieConsent";
import CookieBanner from "components/CookieConsent/CookieBanner";
import { getConsent, applyConsent } from "utils/consent";
import { trackEvent } from "utils/analytics";

// react-router components
import React, { Suspense } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Kit 2 React themes
import theme from "assets/theme";

const BlogArticle = React.lazy(() => import("layouts/pages/blog"));
const BlogList = React.lazy(() => import("pages/Blog/BlogList"));
const PoliticaConfidentialitate = React.lazy(() =>
    import("layouts/pages/politicaConfidentialitate")
);
const TermeneSiConditii = React.lazy(() => import("layouts/pages/termeneSiConditii"));
const PoliticaCookies = React.lazy(() => import("pages/PoliticaCookies"));
const ParcursulInCuratenie = React.lazy(() => import("pages/ParcursulInCuratenie"));
const Sitemap = React.lazy(() => import("pages/Sitemap"));
const Feedback = React.lazy(() => import("pages/Feedback"));

// Material Kit 2 React routes
import routes from "routes";
import footerRoutes from "footer.routes";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import logo from "assets/images/logo.webp";
import Fab from "@mui/material/Fab";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

export default function App() {
    const { pathname } = useLocation();
    const phone = "40730407322";
    const defaultMsg = "Salut! Am o intrebare";

    const { consent, bannerVisible, acceptAll, rejectAll, saveConsent } = useCookieConsent();

    useEffect(() => {
        applyConsent(getConsent());
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            const a = e.target.closest?.("a[href]");
            if (!a) return;
            const href = a.getAttribute("href") || "";

            if (href.startsWith("tel:")) {
                const brut = href.replace("tel:", "").replace(/[\s-]/g, "");
                const numar = brut.startsWith("0") ? "+4" + brut : brut;

                trackEvent("cv_click_telefon", {
                    numar,
                    locatie: a.closest('header, nav, [role="banner"], [class*="MuiAppBar"]')
                        ? "header"
                        : a.closest('footer, [role="contentinfo"]')
                        ? "footer"
                        : "continut",
                    pagina: window.location.pathname,
                });
            } else if (href.includes("wa.me") || href.includes("whatsapp")) {
                trackEvent("cv_click_whatsapp", { pagina: window.location.pathname });
            }
        };
        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, []);

    useEffect(() => {
        import("layouts/pages/presentation");
    }, []);

    // Setting page scroll to 0 when changing the route
    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    const getRoutes = (allRoutes) =>
        allRoutes.map((route) => {
            if (route.collapse) {
                return getRoutes(route.collapse);
            }

            if (route.route) {
                return <Route exact path={route.route} element={route.component} key={route.key} />;
            }

            return null;
        });

    return (
        <Suspense fallback={<div>Loading...</div>}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <DefaultNavbar
                    routes={routes}
                    sticky
                    brandLogo={{ image: logo, name: "Doral Cleaning" }}
                />
                <div style={{ minHeight: "80vh" }} key={"mainContent"}>
                    <Routes>
                        {getRoutes(routes)}
                        <Route path="/presentation" element={<Navigate to="/" replace />} />
                        <Route path="/blog" element={<BlogList />} />
                        <Route path="/blog/" element={<BlogList />} />
                        <Route path="/blog/:slug" element={<BlogArticle />} />
                        <Route path="/blog/:slug/" element={<BlogArticle />} />
                        <Route
                            path="/deseuri-constructii-demolari"
                            element={<Navigate to="/blog/deseuri-constructii-demolari" replace />}
                        />
                        <Route
                            path="/dezinfectie-vs-curatenie"
                            element={<Navigate to="/blog/dezinfectie-vs-curatenie" replace />}
                        />
                        <Route
                            path="/ce-inseamna-curatenie-de-intretinere"
                            element={<Navigate to="/blog/ce-inseamna-curatenie-de-intretinere" replace />}
                        />
                        <Route
                            path="/ce-inseamna-o-firma-de-curatenie-profesionala"
                            element={<Navigate to="/blog/ce-inseamna-o-firma-de-curatenie-profesionala" replace />}
                        />
                        <Route
                            path="/ce-inseamna-curatenia-tehnica"
                            element={<Navigate to="/blog/ce-inseamna-curatenia-tehnica" replace />}
                        />
                        <Route
                            path="/cum-facem-curatenie-in-bucatarie"
                            element={<Navigate to="/blog/cum-facem-curatenie-in-bucatarie" replace />}
                        />
                        <Route
                            path="/externalizare-servicii-curatenie"
                            element={<Navigate to="/blog/externalizare-servicii-curatenie" replace />}
                        />
                        <Route
                            path="/externalizare-servicii-curatenie/"
                            element={<Navigate to="/blog/externalizare-servicii-curatenie" replace />}
                        />
                        <Route
                            path="/new/contact"
                            element={<Navigate to="/contact-curatenie" replace />}
                        />
                        <Route
                            path="/new/contact/"
                            element={<Navigate to="/contact-curatenie" replace />}
                        />
                        <Route path="/autor-blog" element={<Navigate to="/blog" replace />} />
                        <Route path="/politica-cookie" element={<PoliticaCookies />} />
                        <Route
                            path="/politica-confidentialitate"
                            element={<PoliticaConfidentialitate />}
                        />
                        <Route path="/termene-conditii" element={<TermeneSiConditii />} />
                        <Route path="/sitemap" element={<Sitemap />} />
                        <Route path="/tarife-curatenie" element={<Navigate to="/tarife" replace />} />
                        <Route path="/tarife-servicii" element={<Navigate to="/tarife" replace />} />
                        <Route path="/parcursul-in-curatenie" element={<ParcursulInCuratenie />} />
                        <Route path="/feedback" element={<Feedback />} />
                    </Routes>
                </div>
                <DefaultFooter content={footerRoutes} />
                {bannerVisible && (
                    <CookieBanner
                        consent={consent}
                        acceptAll={acceptAll}
                        rejectAll={rejectAll}
                        saveConsent={saveConsent}
                    />
                )}
                <Fab
                    href={`https://wa.me/${phone}?text=${encodeURIComponent(defaultMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp"
                    sx={{
                        position: "fixed",
                        bottom: 34,
                        right: 34,
                        fontSize: 36,
                        bgcolor: "#25D366",
                        color: "#fff",
                        "&:hover": { bgcolor: "#1ebe5d" },
                        boxShadow: "0 6px 12px rgba(0,0,0,.2)",
                        zIndex: 1300,
                    }}
                >
                    <WhatsAppIcon />
                </Fab>
            </ThemeProvider>
        </Suspense>
    );
}
